import { useCallback, useEffect, useState } from "react";
import {
	Badge,
	Box,
	Button,
	Card,
	Divider,
	Group,
	Stack,
	Text,
	Title,
	useMantineColorScheme,
} from "@mantine/core";
import {
	IconBell,
	IconCalendar,
	IconCheck,
	IconChevronLeft,
	IconChevronRight,
	IconCircleCheck,
	IconMapPin,
	IconMicrophone,
	IconSettings,
	IconX,
} from "@tabler/icons-react";
import { SettingsWalkthrough } from "./SettingsWalkthrough";

export type OnboardingStep =
	| "welcome"
	| "notifications-info"
	| "notifications-prompt-preview"
	| "notifications-prompt"
	| "notifications-denied"
	| "location-info"
	| "location-prompt-preview"
	| "location-prompt"
	| "location-denied"
	| "microphone-info"
	| "microphone-prompt-preview"
	| "microphone-prompt"
	| "microphone-denied"
	| "sms-consent"
	| "complete";

export type PermissionStatus = "granted" | "denied" | "not-determined";

export type OnboardingFlowProps = {
	caregiverName: string;
	os: "ios" | "android";
	onRequestNotificationPermission: () => Promise<"granted" | "denied">;
	onRequestLocationPermission: () => Promise<"granted" | "denied">;
	onRequestMicrophonePermission: () => Promise<"granted" | "denied">;
	onSmsConsent: (consent: boolean) => void;
	onOpenAppSettings: () => void;
	onSkipNotifications?: () => void;
	onSkipLocation?: () => void;
	onSkipMicrophone?: () => void;
	onComplete: (permissions: {
		notifications: PermissionStatus;
		location: PermissionStatus;
		microphone: PermissionStatus;
		sms: boolean;
	}) => void | Promise<void>;
	nextShift: {
		clientName: string;
		date: string;
		startTime: string;
		endTime: string;
		durationHours: number;
	} | null;
	initialStep: OnboardingStep;
	initialPermissions: {
		notifications: PermissionStatus;
		location: PermissionStatus;
		microphone: PermissionStatus;
		sms: boolean;
	};
};

export type OnboardingChecklistItem = {
	label: string;
	description: string;
};

export function OnboardingFlow({
	caregiverName,
	os,
	onRequestNotificationPermission,
	onRequestLocationPermission,
	onRequestMicrophonePermission,
	onSmsConsent,
	onOpenAppSettings,
	onSkipNotifications,
	onSkipLocation,
	onSkipMicrophone,
	onComplete,
	nextShift,
	initialStep,
	initialPermissions,
}: OnboardingFlowProps) {
	const allGranted =
		initialPermissions.notifications === "granted" &&
		initialPermissions.location === "granted" &&
		initialPermissions.microphone === "granted" &&
		initialPermissions.sms;
	const [step, setStep] = useState<OnboardingStep>(
		allGranted ? "complete" : initialStep,
	);
	const [notificationsGranted, setNotificationsGranted] = useState(
		initialPermissions.notifications === "granted",
	);
	const [locationGranted, setLocationGranted] = useState(
		initialPermissions.location === "granted",
	);
	const [microphoneGranted, setMicrophoneGranted] = useState(
		initialPermissions.microphone === "granted",
	);
	const [smsConsented, setSmsConsented] = useState(initialPermissions.sms);
	const [saving, setSaving] = useState(false);
	const [recheckFailedNotifications, setRecheckFailedNotifications] =
		useState(false);
	const [recheckFailedLocation, setRecheckFailedLocation] = useState(false);
	const [recheckFailedMicrophone, setRecheckFailedMicrophone] = useState(false);

	useEffect(() => {
		setNotificationsGranted(initialPermissions.notifications === "granted");
		setLocationGranted(initialPermissions.location === "granted");
		setMicrophoneGranted(initialPermissions.microphone === "granted");
		setSmsConsented(initialPermissions.sms);

		const nowAllGranted =
			initialPermissions.notifications === "granted" &&
			initialPermissions.location === "granted" &&
			initialPermissions.microphone === "granted" &&
			initialPermissions.sms;
		if (nowAllGranted) {
			setStep("complete");
			return;
		}

		setStep((current) => {
			if (
				initialPermissions.notifications === "denied" &&
				(current === "notifications-info" ||
					current === "notifications-prompt-preview" ||
					current === "notifications-prompt")
			) {
				return "notifications-denied";
			}
			if (
				initialPermissions.location === "denied" &&
				(current === "location-info" ||
					current === "location-prompt-preview" ||
					current === "location-prompt")
			) {
				return "location-denied";
			}
			if (
				initialPermissions.microphone === "denied" &&
				(current === "microphone-info" ||
					current === "microphone-prompt-preview" ||
					current === "microphone-prompt")
			) {
				return "microphone-denied";
			}
			return current;
		});
	}, [initialPermissions]);

	const handleNotificationPermission = useCallback(async () => {
		const result = await onRequestNotificationPermission();
		setNotificationsGranted(result === "granted");
		if (result === "granted") {
			if (initialPermissions.location === "granted") {
				if (initialPermissions.sms) {
					setStep("complete");
				} else {
					setStep("sms-consent");
				}
			} else {
				setStep("location-info");
			}
		} else {
			setStep("notifications-denied");
		}
	}, [
		onRequestNotificationPermission,
		initialPermissions.location,
		initialPermissions.sms,
	]);

	const handleLocationPermission = useCallback(async () => {
		const result = await onRequestLocationPermission();
		setLocationGranted(result === "granted");
		if (result === "granted") {
			if (initialPermissions.microphone === "granted") {
				if (initialPermissions.sms) {
					setStep("complete");
				} else {
					setStep("sms-consent");
				}
			} else {
				setStep("microphone-info");
			}
		} else {
			setStep("location-denied");
		}
	}, [
		onRequestLocationPermission,
		initialPermissions.microphone,
		initialPermissions.sms,
	]);

	const handleNotificationRecheck = useCallback(async () => {
		const result = await onRequestNotificationPermission();
		setNotificationsGranted(result === "granted");
		if (result === "granted") {
			setRecheckFailedNotifications(false);
			if (initialPermissions.location === "granted") {
				if (initialPermissions.sms) {
					setStep("complete");
				} else {
					setStep("sms-consent");
				}
			} else {
				setStep("location-info");
			}
		} else {
			setRecheckFailedNotifications(true);
		}
	}, [
		onRequestNotificationPermission,
		initialPermissions.location,
		initialPermissions.sms,
	]);

	const handleLocationRecheck = useCallback(async () => {
		const result = await onRequestLocationPermission();
		setLocationGranted(result === "granted");
		if (result === "granted") {
			setRecheckFailedLocation(false);
			if (initialPermissions.microphone === "granted") {
				if (initialPermissions.sms) {
					setStep("complete");
				} else {
					setStep("sms-consent");
				}
			} else {
				setStep("microphone-info");
			}
		} else {
			setRecheckFailedLocation(true);
		}
	}, [
		onRequestLocationPermission,
		initialPermissions.microphone,
		initialPermissions.sms,
	]);

	const getFirstNotificationsStep = useCallback((): OnboardingStep => {
		if (initialPermissions.notifications === "denied") {
			return "notifications-denied";
		}
		return "notifications-info";
	}, [initialPermissions.notifications]);

	const getFirstLocationStep = useCallback((): OnboardingStep => {
		if (initialPermissions.location === "denied") {
			return "location-denied";
		}
		return "location-info";
	}, [initialPermissions.location]);

	const getFirstMicrophoneStep = useCallback((): OnboardingStep => {
		if (initialPermissions.microphone === "denied") {
			return "microphone-denied";
		}
		return "microphone-info";
	}, [initialPermissions.microphone]);

	const handleMicrophonePermission = useCallback(async () => {
		const result = await onRequestMicrophonePermission();
		setMicrophoneGranted(result === "granted");
		if (result === "granted") {
			if (initialPermissions.sms) {
				setStep("complete");
			} else {
				setStep("sms-consent");
			}
		} else {
			setStep("microphone-denied");
		}
	}, [onRequestMicrophonePermission, initialPermissions.sms]);

	const handleMicrophoneRecheck = useCallback(async () => {
		const result = await onRequestMicrophonePermission();
		setMicrophoneGranted(result === "granted");
		if (result === "granted") {
			setRecheckFailedMicrophone(false);
			if (initialPermissions.sms) {
				setStep("complete");
			} else {
				setStep("sms-consent");
			}
		} else {
			setRecheckFailedMicrophone(true);
		}
	}, [onRequestMicrophonePermission, initialPermissions.sms]);

	const currentStepIndex = getStepIndex(step);

	const needsNotifications = initialPermissions.notifications !== "granted";
	const needsLocation = initialPermissions.location !== "granted";
	const needsMicrophone = initialPermissions.microphone !== "granted";
	const needsSms = !initialPermissions.sms;
	const totalSteps = [
		needsNotifications,
		needsLocation,
		needsMicrophone,
		needsSms,
	].filter(Boolean).length;

	const getStepLabel = (
		permission: "notifications" | "location" | "microphone" | "sms",
	): string => {
		let stepNum = 0;
		if (permission === "notifications") {
			stepNum = 1;
		} else if (permission === "location") {
			stepNum = needsNotifications ? 2 : 1;
		} else if (permission === "microphone") {
			stepNum = (needsNotifications ? 1 : 0) + (needsLocation ? 1 : 0) + 1;
		} else {
			stepNum =
				(needsNotifications ? 1 : 0) +
				(needsLocation ? 1 : 0) +
				(needsMicrophone ? 1 : 0) +
				1;
		}
		const suffix =
			permission === "notifications"
				? "Notifications"
				: permission === "location"
					? "Location"
					: permission === "microphone"
						? "Microphone"
						: "SMS";
		return `Step ${stepNum} of ${totalSteps} · ${suffix}`;
	};

	const content = (() => {
		switch (step) {
			case "welcome": {
				const setupItems: {
					number: number;
					label: string;
					description: string;
				}[] = [];
				let itemNum = 1;
				if (needsNotifications) {
					setupItems.push({
						number: itemNum++,
						label: "Push notifications",
						description:
							"Shift offers, schedule reminders, messages from your coordinator.",
					});
				}
				if (needsLocation) {
					setupItems.push({
						number: itemNum++,
						label: "Location while clocking in and out",
						description: "So we can verify your visit.",
					});
				}
				if (needsMicrophone) {
					setupItems.push({
						number: itemNum++,
						label: "Microphone for verbal confirmations",
						description: "To record client confirmation of their visit.",
					});
				}
				if (needsSms) {
					setupItems.push({
						number: itemNum++,
						label: "SMS Consent",
						description: "Important information texted to your phone.",
					});
				}
				const totalSetups = setupItems.length;
				const title =
					totalSetups === 1
						? "One quick setup before your shift."
						: totalSetups === 2
							? "Two quick setups before your shift."
							: "Three quick setups before your shift.";
				const subtitle =
					totalSetups === 1
						? "We'll ask your phone for one permission. Takes about 15 seconds."
						: totalSetups === 2
							? "We'll ask your phone for two permissions. Takes about 30 seconds."
							: "We'll set a few things up. Takes about 45 seconds.";

				const handleGetStarted = () => {
					if (needsNotifications) {
						setStep(getFirstNotificationsStep());
					} else if (needsLocation) {
						setStep(getFirstLocationStep());
					} else if (needsMicrophone) {
						setStep(getFirstMicrophoneStep());
					} else {
						setStep("sms-consent");
					}
				};

				return (
					<OnboardingPageShell
						badge={`Welcome, ${caregiverName}`}
						currentStep={currentStepIndex}
					>
						<Stack gap="md" style={{ flex: 1 }} justify="space-between">
							<Stack gap="md">
								<Title order={1} fw={800} lh={1.1}>
									{title}
								</Title>
								<Text fz="sm">{subtitle}</Text>
								<Stack gap="sm">
									{setupItems.map((item) => (
										<SetupCard
											key={item.label}
											number={item.number}
											label={item.label}
											description={item.description}
										/>
									))}
								</Stack>
							</Stack>
							<Button
								fullWidth
								color="dark"
								size="lg"
								radius="xl"
								onClick={handleGetStarted}
							>
								Get started
							</Button>
						</Stack>
					</OnboardingPageShell>
				);
			}

			case "notifications-info":
				return (
					<OnboardingPageShell
						badge={getStepLabel("notifications")}
						currentStep={currentStepIndex}
						onBack={() => setStep("welcome")}
					>
						<Stack gap="md" style={{ flex: 1 }} justify="space-between">
							<Stack gap="md">
								<Title order={1} fw={800} lh={1.1}>
									Enable notifications to stay in the loop.
								</Title>
								<Text fz="sm">
									Notifications are how we reach you when timing matters.
								</Text>
								<Stack gap="sm">
									<ChecklistItem
										label="Messages from your coordinator"
										description="Important updates from the office."
									/>
									<ChecklistItem
										label="Open shift offers"
										description="First to claim wins - be the first to know."
									/>
									<ChecklistItem
										label="Schedule reminders"
										description="A nudge 15 minutes before each shift starts and ends."
									/>
								</Stack>
							</Stack>
							<Button
								fullWidth
								color="dark"
								size="lg"
								radius="xl"
								onClick={() => setStep("notifications-prompt-preview")}
							>
								Show me the prompt
							</Button>
						</Stack>
					</OnboardingPageShell>
				);

			case "notifications-prompt-preview":
				return (
					<OnboardingNotificationsHeadsUpPage
						os={os}
						onReady={() => handleNotificationPermission()}
						onBack={() => setStep("notifications-info")}
					/>
				);

			case "notifications-denied":
				return (
					<OnboardingPermissionDeniedPage
						permission="notifications"
						os={os}
						currentStep={currentStepIndex}
						onOpenSettings={onOpenAppSettings}
						onRecheck={handleNotificationRecheck}
						onSkip={() => {
							onSkipNotifications?.();
							if (initialPermissions.location === "granted") {
								if (initialPermissions.sms) {
									setStep("complete");
								} else {
									setStep("sms-consent");
								}
							} else {
								setStep(getFirstLocationStep());
							}
						}}
						onBack={() => setStep("notifications-info")}
						recheckFailed={recheckFailedNotifications}
						badge={getStepLabel("notifications")}
					/>
				);

			case "location-info":
				return (
					<OnboardingPageShell
						badge={getStepLabel("location")}
						currentStep={currentStepIndex}
						onBack={() => setStep(getFirstNotificationsStep())}
					>
						<Stack gap="md" style={{ flex: 1 }} justify="space-between">
							<Stack gap="md">
								<Title order={1} fw={800} lh={1.1}>
									Allow location so we can verify your visit.
								</Title>
								<Text fz="sm">
									Electronic Visit Verification (EVV) is required for
									compliance. We use location only at clock-in and clock-out -
									never in the background.
								</Text>
								<Stack gap="sm">
									<ChecklistItem
										label="Only when you clock in or out"
										description="The app reads your location for a few seconds and stops."
									/>
									<ChecklistItem
										label="Never while the app is closed"
										description="We don't track you between visits."
									/>
									<ChecklistItem
										label="Required for your shift"
										description="The visit can't be verified without it."
									/>
								</Stack>
							</Stack>
							<Button
								fullWidth
								color="dark"
								size="lg"
								radius="xl"
								onClick={() => setStep("location-prompt-preview")}
							>
								Show me the prompt
							</Button>
						</Stack>
					</OnboardingPageShell>
				);

			case "location-prompt-preview":
				return (
					<OnboardingLocationHeadsUpPage
						os={os}
						onReady={() => handleLocationPermission()}
						onBack={() => setStep("location-info")}
					/>
				);

			case "location-denied":
				return (
					<OnboardingPermissionDeniedPage
						permission="location"
						os={os}
						currentStep={currentStepIndex}
						onOpenSettings={onOpenAppSettings}
						onRecheck={handleLocationRecheck}
						onSkip={() => {
							onSkipLocation?.();
							if (initialPermissions.microphone === "granted") {
								if (initialPermissions.sms) {
									setStep("complete");
								} else {
									setStep("sms-consent");
								}
							} else {
								setStep(getFirstMicrophoneStep());
							}
						}}
						onBack={() => setStep("location-info")}
						recheckFailed={recheckFailedLocation}
						badge={getStepLabel("location")}
					/>
				);

			case "microphone-info":
				return (
					<OnboardingPageShell
						badge={getStepLabel("microphone")}
						currentStep={currentStepIndex}
						onBack={() => setStep(getFirstLocationStep())}
					>
						<Stack gap="md" style={{ flex: 1 }} justify="space-between">
							<Stack gap="md">
								<Title order={1} fw={800} lh={1.1}>
									Allow microphone to record client confirmations.
								</Title>
								<Text fz="sm">
									Some visits require a verbal confirmation from the client. We
									use the microphone only when you tap "Record" during checkout.
								</Text>
								<Stack gap="sm">
									<ChecklistItem
										label="Only when you tap Record"
										description="The microphone is only active during check out when you press the record button."
									/>
									<ChecklistItem
										label="Never in the background"
										description="We never record without your action."
									/>
									<ChecklistItem
										label="Required for visit verification"
										description="The client's verbal confirmation replaces a written signature."
									/>
								</Stack>
							</Stack>
							<Button
								fullWidth
								color="dark"
								size="lg"
								radius="xl"
								onClick={() => setStep("microphone-prompt-preview")}
							>
								Show me the prompt
							</Button>
						</Stack>
					</OnboardingPageShell>
				);

			case "microphone-prompt-preview":
				return (
					<OnboardingMicrophoneHeadsUpPage
						os={os}
						onReady={() => handleMicrophonePermission()}
						onBack={() => setStep("microphone-info")}
					/>
				);

			case "microphone-denied":
				return (
					<OnboardingPermissionDeniedPage
						permission="microphone"
						os={os}
						currentStep={currentStepIndex}
						onOpenSettings={onOpenAppSettings}
						onRecheck={handleMicrophoneRecheck}
						onSkip={() => {
							onSkipMicrophone?.();
							if (initialPermissions.sms) {
								setStep("complete");
							} else {
								setStep("sms-consent");
							}
						}}
						onBack={() => setStep("microphone-info")}
						recheckFailed={recheckFailedMicrophone}
						badge={getStepLabel("microphone")}
					/>
				);

			case "sms-consent":
				return (
					<OnboardingPageShell
						badge={getStepLabel("sms")}
						currentStep={currentStepIndex}
						onBack={() => setStep(getFirstMicrophoneStep())}
					>
						<Stack gap="md" style={{ flex: 1 }} justify="space-between">
							<Stack gap="md">
								<Title order={1} fw={800} lh={1.1}>
									Get important info texted to your phone.
								</Title>
								<Text fz="sm">
									By pressing <strong>Continue</strong>, you agree to Village
									Caregiving's{" "}
									<Text
										component="a"
										href="/vcg-mobile-app-terms-of-service.html"
										target="_blank"
										fz="sm"
										td="underline"
										c="inherit"
									>
										Terms of Service
									</Text>{" "}
									and{" "}
									<Text
										component="a"
										href="/vcg-mobile-app-privacy-policy.html"
										target="_blank"
										fz="sm"
										td="underline"
										c="inherit"
									>
										Privacy Policy
									</Text>
									. You consent to receive SMS messages from Village Caregiving
									at your number on file for check-in and check-out reminders,
									chat messages, and verification codes. Message frequency
									depends on your activity. You may opt out by texting STOP.
									Text HELP for support. Message and data rates may apply.
								</Text>
							</Stack>
							<Button
								fullWidth
								color="dark"
								size="lg"
								radius="xl"
								onClick={() => {
									onSmsConsent(true);
									setSmsConsented(true);
									setStep("complete");
								}}
							>
								Continue
							</Button>
						</Stack>
					</OnboardingPageShell>
				);

			case "complete": {
				const allCoreGranted =
					notificationsGranted && locationGranted && microphoneGranted;
				const someCoreGranted =
					notificationsGranted || locationGranted || microphoneGranted;
				const permissionStatusText = getPermissionStatusText(
					notificationsGranted,
					locationGranted,
					microphoneGranted,
					nextShift !== null && allCoreGranted,
				);
				const completionTitle = getCompletionTitle(
					allCoreGranted,
					someCoreGranted,
					nextShift !== null,
				);
				const completionBadge = allCoreGranted
					? "You're ready"
					: "Final Review";
				return (
					<OnboardingPageShell
						badge={completionBadge}
						currentStep={currentStepIndex}
					>
						<Stack gap="md" style={{ flex: 1 }} justify="space-between">
							<Stack gap="md">
								<Title order={1} fw={800} lh={1.1}>
									{completionTitle}
								</Title>
								<Text fz="sm">{permissionStatusText}</Text>
								{nextShift ? (
									<Card withBorder radius="md" p="md">
										<Stack gap="xs">
											<Group justify="space-between" align="flex-start">
												<Stack gap={2}>
													<Text size="xs" c="dimmed" tt="uppercase" fw={600}>
														{nextShift.date}
													</Text>
													<Title order={3} fw={700}>
														{nextShift.clientName}
													</Title>
												</Stack>
												<Badge circle size="lg" color="gray" variant="light">
													{nextShift.durationHours}h
												</Badge>
											</Group>
											<Divider />
											<Group gap="xl">
												<Stack gap={2}>
													<Text size="xs" c="dimmed" tt="uppercase" fw={600}>
														Start
													</Text>
													<Text fw={600}>{nextShift.startTime}</Text>
												</Stack>
												<Stack gap={2}>
													<Text size="xs" c="dimmed" tt="uppercase" fw={600}>
														End
													</Text>
													<Text fw={600}>{nextShift.endTime}</Text>
												</Stack>
											</Group>
										</Stack>
									</Card>
								) : (
									<Card withBorder radius="md" p="md">
										<Stack gap="xs" align="center" py="md">
											<IconCalendar
												size={32}
												color="var(--mantine-color-dimmed)"
												stroke={1.5}
											/>
											<Text fw={600} ta="center">
												Waiting for your next assignment
											</Text>
											<Text size="sm" c="dimmed" ta="center">
												Your coordinator will assign your next visit soon.
											</Text>
										</Stack>
									</Card>
								)}
								<Stack gap="xs">
									<CompletionCheckItem
										label="Push notifications enabled"
										checked={notificationsGranted}
										onFix={() => setStep("notifications-denied")}
									/>
									<CompletionCheckItem
										label="Location while using app"
										checked={locationGranted}
										onFix={() => setStep("location-denied")}
									/>
									<CompletionCheckItem
										label="Microphone for confirmations"
										checked={microphoneGranted}
										onFix={() => setStep("microphone-denied")}
									/>
									<CompletionCheckItem
										label="SMS Consent"
										checked={smsConsented}
										onFix={() => setStep("sms-consent")}
									/>
								</Stack>
							</Stack>
							<Stack gap="xs">
								<Button
									fullWidth
									color="dark"
									size="lg"
									radius="xl"
									loading={saving}
									onClick={async () => {
										setSaving(true);
										try {
											await onComplete({
												notifications: notificationsGranted
													? "granted"
													: "denied",
												location: locationGranted ? "granted" : "denied",
												microphone: microphoneGranted ? "granted" : "denied",
												sms: smsConsented,
											});
										} finally {
											setSaving(false);
										}
									}}
								>
									{nextShift ? "Open my shift" : "Open my schedule"}
								</Button>
								{nextShift && (
									<Text size="xs" c="dimmed" ta="center">
										Clock-in opens 15 min before start time
									</Text>
								)}
							</Stack>
						</Stack>
					</OnboardingPageShell>
				);
			}

			default:
				return null;
		}
	})();

	return (
		<>
			<style>{shimmerKeyframes}</style>
			{content}
		</>
	);
}

const STEP_DOT_KEYS = [
	"welcome",
	"notifications",
	"location",
	"microphone",
	"sms",
	"complete",
];

function OnboardingPageShell({
	badge,
	currentStep,
	onBack,
	children,
}: {
	badge: string;
	currentStep: number;
	onBack?: () => void;
	children: React.ReactNode;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Stack gap="md" p="md" style={{ flex: 1, overflowY: "auto" }}>
			<Group justify="space-between" align="center">
				<Group gap="xs">
					{onBack && (
						<Button
							variant="subtle"
							color={isDark ? "gray" : "dark"}
							size="compact-xs"
							px={4}
							onClick={onBack}
						>
							<IconChevronLeft size={18} />
						</Button>
					)}
					<Badge color={isDark ? "gray" : "dark"} variant="filled" radius="xl">
						{badge}
					</Badge>
				</Group>
				<Group gap={4}>
					{STEP_DOT_KEYS.map((key, i) => (
						<Box
							key={key}
							style={{
								width: i === currentStep ? 20 : 8,
								height: 8,
								borderRadius: 4,
								backgroundColor:
									i === currentStep
										? "var(--mantine-color-text)"
										: "var(--mantine-color-default-border)",
								transition: "width 200ms ease",
							}}
						/>
					))}
				</Group>
			</Group>
			{children}
		</Stack>
	);
}

function SetupCard({
	number,
	label,
	description,
}: {
	number: number;
	label: string;
	description: string;
}) {
	return (
		<Card withBorder radius="md" p="md">
			<Group wrap="nowrap" gap="sm" align="flex-start">
				<Box
					style={{
						width: 32,
						height: 32,
						borderRadius: "50%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
						backgroundColor: "var(--mantine-color-text)",
					}}
				>
					<Text fw={700} size="sm" c="var(--mantine-color-body)">
						{number}
					</Text>
				</Box>
				<Stack gap={2}>
					<Text fw={600}>{label}</Text>
					<Text size="sm" c="dimmed">
						{description}
					</Text>
				</Stack>
			</Group>
		</Card>
	);
}

function ChecklistItem({ label, description }: OnboardingChecklistItem) {
	return (
		<Group wrap="nowrap" gap="sm" align="flex-start">
			<IconCircleCheck
				size={24}
				color="var(--mantine-color-text)"
				style={{ flexShrink: 0, marginTop: 2 }}
			/>
			<Stack gap={2}>
				<Text fw={600}>{label}</Text>
				<Text size="sm" c="dimmed">
					{description}
				</Text>
			</Stack>
		</Group>
	);
}

function CompletionCheckItem({
	label,
	checked,
	onFix,
}: {
	label: string;
	checked: boolean;
	onFix?: () => void;
}) {
	return (
		<Group wrap="nowrap" gap="sm" justify="space-between">
			<Group wrap="nowrap" gap="sm">
				{checked ? (
					<Box
						style={{
							width: 24,
							height: 24,
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
							backgroundColor: "var(--mantine-color-blue-6)",
						}}
					>
						<IconCheck size={14} color="white" />
					</Box>
				) : (
					<Box
						style={{
							width: 24,
							height: 24,
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
							backgroundColor: "var(--mantine-color-red-6)",
						}}
					>
						<IconX size={14} color="white" />
					</Box>
				)}
				<Text fw={500} c={checked ? undefined : "dimmed"}>
					{label}
				</Text>
			</Group>
			{!checked && onFix && (
				<Button
					variant="outline"
					color="red"
					size="compact-sm"
					radius="xl"
					onClick={onFix}
				>
					Fix it
				</Button>
			)}
		</Group>
	);
}

function OnboardingNotificationsHeadsUpPage({
	os,
	onReady,
	onBack,
}: {
	os: "ios" | "android";
	onReady: () => void;
	onBack: () => void;
}) {
	return (
		<Stack
			gap="sm"
			p="md"
			justify="space-between"
			style={{
				flex: 1,
				backgroundColor: "var(--mantine-color-dark-9)",
				color: "white",
				overflowY: "auto",
			}}
		>
			<Stack gap="sm">
				<Group gap="xs">
					<Button
						variant="subtle"
						color="gray"
						size="compact-xs"
						px={4}
						onClick={onBack}
					>
						<IconChevronLeft size={18} color="white" />
					</Button>
					<Badge
						variant="outline"
						radius="xl"
						styles={{ root: { borderColor: "white", color: "white" } }}
					>
						Heads up
					</Badge>
				</Group>
				<Title order={2} fw={800} lh={1.1} c="white">
					This is what you'll see next.
				</Title>
				<Text size="sm" c="gray.4">
					When you tap <strong style={{ color: "white" }}>I'm ready</strong>,{" "}
					you'll see the popup shown below. Tap{" "}
					<strong style={{ color: "white" }}>Allow</strong>.
				</Text>
			</Stack>
			<NotificationDialogPreview os={os} />
			<Button
				fullWidth
				color="white"
				variant="white"
				c="dark"
				size="lg"
				radius="xl"
				onClick={onReady}
				rightSection={<IconChevronRight size={18} />}
			>
				I'm ready
			</Button>
		</Stack>
	);
}

function OnboardingLocationHeadsUpPage({
	os,
	onReady,
	onBack,
}: {
	os: "ios" | "android";
	onReady: () => void;
	onBack: () => void;
}) {
	return (
		<Stack
			gap="sm"
			p="md"
			justify="space-between"
			style={{
				flex: 1,
				backgroundColor: "var(--mantine-color-dark-9)",
				color: "white",
				overflowY: "auto",
			}}
		>
			<Stack gap="sm">
				<Group gap="xs">
					<Button
						variant="subtle"
						color="gray"
						size="compact-xs"
						px={4}
						onClick={onBack}
					>
						<IconChevronLeft size={18} color="white" />
					</Button>
					<Badge
						variant="outline"
						radius="xl"
						styles={{ root: { borderColor: "white", color: "white" } }}
					>
						Heads up
					</Badge>
				</Group>
				<Title order={2} fw={800} lh={1.1} c="white">
					This is what you'll see next.
				</Title>
				<Text size="sm" c="gray.4">
					When you tap <strong style={{ color: "white" }}>I'm ready</strong>,{" "}
					you'll see the popup shown below. Enable{" "}
					<strong style={{ color: "white" }}>Precise</strong> and tap{" "}
					<strong style={{ color: "white" }}>
						{os === "android" ? "While using the app" : "Allow While Using App"}
					</strong>{" "}
					.
				</Text>
			</Stack>
			<LocationDialogPreview os={os} />
			<Button
				fullWidth
				color="white"
				variant="white"
				c="dark"
				size="lg"
				radius="xl"
				onClick={onReady}
				rightSection={<IconChevronRight size={18} />}
			>
				I'm ready
			</Button>
		</Stack>
	);
}

function OnboardingMicrophoneHeadsUpPage({
	os,
	onReady,
	onBack,
}: {
	os: "ios" | "android";
	onReady: () => void;
	onBack: () => void;
}) {
	return (
		<Stack
			gap="sm"
			p="md"
			justify="space-between"
			style={{
				flex: 1,
				backgroundColor: "var(--mantine-color-dark-9)",
				color: "white",
				overflowY: "auto",
			}}
		>
			<Stack gap="sm">
				<Group gap="xs">
					<Button
						variant="subtle"
						color="gray"
						size="compact-xs"
						px={4}
						onClick={onBack}
					>
						<IconChevronLeft size={18} color="white" />
					</Button>
					<Badge
						variant="outline"
						radius="xl"
						styles={{ root: { borderColor: "white", color: "white" } }}
					>
						Heads up
					</Badge>
				</Group>
				<Title order={2} fw={800} lh={1.1} c="white">
					This is what you'll see next.
				</Title>
				<Text size="sm" c="gray.4">
					When you tap <strong style={{ color: "white" }}>I'm ready</strong>,{" "}
					you'll see the popup shown below. Tap{" "}
					<strong style={{ color: "white" }}>
						{os === "android" ? "While using the app" : "Allow"}
					</strong>
					.
				</Text>
			</Stack>
			<MicrophoneDialogPreview os={os} />
			<Button
				fullWidth
				color="white"
				variant="white"
				c="dark"
				size="lg"
				radius="xl"
				onClick={onReady}
				rightSection={<IconChevronRight size={18} />}
			>
				I'm ready
			</Button>
		</Stack>
	);
}

function MicrophoneDialogPreview({
	os,
}: {
	os: "ios" | "android";
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	if (os === "android") {
		return (
			<Card
				radius="xl"
				p="lg"
				bg={
					isDark ? "var(--mantine-color-dark-6)" : "var(--mantine-color-gray-1)"
				}
			>
				<Stack align="center" gap="md">
					<IconMicrophone
						size={28}
						color={
							isDark
								? "var(--mantine-color-indigo-4)"
								: "var(--mantine-color-indigo-6)"
						}
					/>
					<Text size="md" ta="center" c={isDark ? "gray.3" : "dark"}>
						Allow{" "}
						<Text span size="md" fw={700}>
							Village Caregiving
						</Text>{" "}
						to record audio?
					</Text>
					<Stack gap="sm" w="100%">
						<HighlightedButton>
							<Button
								fullWidth
								radius="xl"
								size="xs"
								variant="filled"
								color={isDark ? "dark.4" : "indigo.1"}
								c={isDark ? "indigo.3" : "indigo.9"}
								fw={600}
								tabIndex={-1}
							>
								While using the app
							</Button>
						</HighlightedButton>
						<Button
							fullWidth
							radius="xl"
							size="xs"
							variant="filled"
							color={isDark ? "dark.4" : "indigo.1"}
							c={isDark ? "indigo.3" : "indigo.9"}
							tabIndex={-1}
						>
							Only this time
						</Button>
						<Button
							fullWidth
							radius="xl"
							size="xs"
							variant="filled"
							color={isDark ? "dark.4" : "indigo.1"}
							c={isDark ? "indigo.3" : "indigo.9"}
							tabIndex={-1}
						>
							Don't allow
						</Button>
					</Stack>
				</Stack>
			</Card>
		);
	}

	return (
		<Card
			radius="md"
			p={0}
			bg={isDark ? "var(--mantine-color-dark-5)" : "white"}
			shadow="lg"
		>
			<Stack align="center" gap={0}>
				<Stack align="center" gap={4} p="md" pb="sm">
					<Text
						size="md"
						fw={600}
						ta="center"
						c={isDark ? "white" : "dark"}
						lh={1.3}
					>
						"Village Caregiving" Would Like to Access the Microphone
					</Text>
					<Text size="xs" ta="center" c={isDark ? "gray.4" : "dimmed"}>
						To record verbal confirmations from clients during visit checkout.
					</Text>
				</Stack>
				<Divider w="100%" color={isDark ? "dark.3" : "gray.3"} />
				<Group wrap="nowrap" gap={0} w="100%" style={{ minHeight: 44 }}>
					<Box style={{ flex: 1 }}>
						<Button
							fullWidth
							variant="subtle"
							color="blue"
							radius={0}
							tabIndex={-1}
						>
							Don't Allow
						</Button>
					</Box>
					<Divider
						orientation="vertical"
						color={isDark ? "dark.3" : "gray.3"}
						style={{ alignSelf: "stretch" }}
					/>
					<Box
						style={{
							flex: 1,
							alignSelf: "stretch",
							display: "flex",
							alignItems: "center",
							animation: isDark
								? "iosBgPulseDark 2.5s ease-in-out infinite"
								: "iosBgPulse 2.5s ease-in-out infinite",
						}}
					>
						<Button
							fullWidth
							variant="subtle"
							color="blue"
							fw={600}
							radius={0}
							tabIndex={-1}
						>
							OK
						</Button>
					</Box>
				</Group>
			</Stack>
		</Card>
	);
}

const shimmerKeyframes = `
@keyframes shimmerBorder {
	0% { background-position: -200% center; }
	100% { background-position: 200% center; }
}
@keyframes iosBgPulse {
	0%, 100% { background-color: rgba(59,130,246,0.06); }
	50% { background-color: rgba(59,130,246,0.15); }
}
@keyframes iosBgPulseDark {
	0%, 100% { background-color: rgba(129,140,248,0.08); }
	50% { background-color: rgba(129,140,248,0.2); }
}`;

function HighlightedButton({
	children,
}: {
	children: React.ReactNode;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Box style={{ width: "100%" }}>
			<Box
				style={{
					borderRadius: 999,
					padding: 2,
					background: isDark
						? "linear-gradient(90deg, rgba(129,140,248,0.2) 0%, rgba(129,140,248,0.7) 25%, rgba(129,140,248,0.3) 50%, rgba(129,140,248,0.7) 75%, rgba(129,140,248,0.2) 100%)"
						: "linear-gradient(90deg, rgba(59,130,246,0.2) 0%, rgba(147,197,253,0.9) 25%, rgba(59,130,246,0.4) 50%, rgba(147,197,253,0.9) 75%, rgba(59,130,246,0.2) 100%)",
					backgroundSize: "200% 100%",
					animation: "shimmerBorder 3.5s linear infinite",
				}}
			>
				<Box
					style={{
						borderRadius: 999,
						backgroundColor: isDark ? "var(--mantine-color-dark-6)" : "white",
					}}
				>
					{children}
				</Box>
			</Box>
		</Box>
	);
}

function NotificationDialogPreview({
	os,
}: {
	os: "ios" | "android";
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	if (os === "android") {
		return (
			<Card
				radius="xl"
				p="lg"
				bg={
					isDark ? "var(--mantine-color-dark-6)" : "var(--mantine-color-gray-1)"
				}
			>
				<Stack align="center" gap="md">
					<IconBell
						size={28}
						color={
							isDark
								? "var(--mantine-color-indigo-4)"
								: "var(--mantine-color-indigo-6)"
						}
					/>
					<Text size="md" ta="center" c={isDark ? "gray.3" : "dark"}>
						Allow{" "}
						<Text span size="md" fw={700}>
							Village Caregiving
						</Text>{" "}
						to send you notifications?
					</Text>
					<Stack gap="sm" w="100%">
						<HighlightedButton>
							<Button
								fullWidth
								radius="xl"
								size="xs"
								variant="filled"
								color={isDark ? "dark.4" : "indigo.1"}
								c={isDark ? "indigo.3" : "indigo.9"}
								fw={600}
								tabIndex={-1}
							>
								Allow
							</Button>
						</HighlightedButton>
						<Button
							fullWidth
							radius="xl"
							size="xs"
							variant="filled"
							color={isDark ? "dark.4" : "indigo.1"}
							c={isDark ? "indigo.3" : "indigo.9"}
							tabIndex={-1}
						>
							Don't allow
						</Button>
					</Stack>
				</Stack>
			</Card>
		);
	}

	return (
		<Card
			radius="md"
			p={0}
			bg={isDark ? "var(--mantine-color-dark-5)" : "white"}
			shadow="lg"
		>
			<Stack align="center" gap={0}>
				<Stack align="center" gap={4} p="md" pb="sm">
					<Text
						size="md"
						fw={600}
						ta="center"
						c={isDark ? "white" : "dark"}
						lh={1.3}
					>
						"Village Caregiving" Would Like to Send You Notifications
					</Text>
					<Text size="xs" ta="center" c={isDark ? "gray.4" : "dimmed"}>
						Notifications may include alerts, sounds, and icon badges. These can
						be configured in Settings.
					</Text>
				</Stack>
				<Divider w="100%" color={isDark ? "dark.3" : "gray.3"} />
				<Group wrap="nowrap" gap={0} w="100%" style={{ minHeight: 44 }}>
					<Box style={{ flex: 1 }}>
						<Button
							fullWidth
							variant="subtle"
							color="blue"
							radius={0}
							tabIndex={-1}
						>
							Don't Allow
						</Button>
					</Box>
					<Divider
						orientation="vertical"
						color={isDark ? "dark.3" : "gray.3"}
						style={{ alignSelf: "stretch" }}
					/>
					<Box
						style={{
							flex: 1,
							alignSelf: "stretch",
							display: "flex",
							alignItems: "center",
							animation: isDark
								? "iosBgPulseDark 2.5s ease-in-out infinite"
								: "iosBgPulse 2.5s ease-in-out infinite",
						}}
					>
						<Button
							fullWidth
							variant="subtle"
							color="blue"
							fw={600}
							radius={0}
							tabIndex={-1}
						>
							Allow
						</Button>
					</Box>
				</Group>
			</Stack>
		</Card>
	);
}

function LocationDialogPreview({
	os,
}: {
	os: "ios" | "android";
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	if (os === "android") {
		return (
			<Card
				radius="xl"
				p="md"
				bg={
					isDark ? "var(--mantine-color-dark-6)" : "var(--mantine-color-gray-1)"
				}
			>
				<Stack align="center" gap="xs">
					<IconMapPin
						size={24}
						color={
							isDark
								? "var(--mantine-color-indigo-4)"
								: "var(--mantine-color-indigo-6)"
						}
					/>
					<Text size="sm" ta="center" c={isDark ? "gray.3" : "dark"}>
						Allow{" "}
						<Text span size="md" fw={700}>
							Village Caregiving
						</Text>{" "}
						to access this device’s location?
					</Text>
					<Group gap="md" justify="center" w="100%">
						<Stack align="center" gap={4}>
							<Box
								style={{
									width: 44,
									height: 44,
									borderRadius: "50%",
									border: `2px solid ${isDark ? "var(--mantine-color-indigo-4)" : "var(--mantine-color-indigo-6)"}`,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<IconMapPin
									size={18}
									color={
										isDark
											? "var(--mantine-color-indigo-4)"
											: "var(--mantine-color-indigo-6)"
									}
								/>
							</Box>
							<Text size="xs" fw={600} c={isDark ? "gray.3" : "dark"}>
								Precise
							</Text>
						</Stack>
						<Stack align="center" gap={4}>
							<Box
								style={{
									width: 44,
									height: 44,
									borderRadius: "50%",
									border: `2px solid ${isDark ? "var(--mantine-color-dark-3)" : "var(--mantine-color-gray-4)"}`,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<IconMapPin
									size={18}
									color={
										isDark
											? "var(--mantine-color-dark-3)"
											: "var(--mantine-color-gray-4)"
									}
								/>
							</Box>
							<Text size="xs" c={isDark ? "gray.5" : "gray.6"}>
								Approximate
							</Text>
						</Stack>
					</Group>
					<Stack gap="xs" w="100%">
						<HighlightedButton>
							<Button
								fullWidth
								radius="xl"
								size="xs"
								variant="filled"
								color={isDark ? "dark.4" : "indigo.1"}
								c={isDark ? "indigo.3" : "indigo.9"}
								fw={600}
								tabIndex={-1}
							>
								While using the app
							</Button>
						</HighlightedButton>
						<Button
							fullWidth
							radius="xl"
							size="xs"
							variant="filled"
							color={isDark ? "dark.4" : "indigo.1"}
							c={isDark ? "indigo.3" : "indigo.9"}
							tabIndex={-1}
						>
							Only this time
						</Button>
						<Button
							fullWidth
							radius="xl"
							size="xs"
							variant="filled"
							color={isDark ? "dark.4" : "indigo.1"}
							c={isDark ? "indigo.3" : "indigo.9"}
							tabIndex={-1}
						>
							Don’t allow
						</Button>
					</Stack>
				</Stack>
			</Card>
		);
	}

	return (
		<Card
			radius="md"
			p={0}
			bg={isDark ? "var(--mantine-color-dark-5)" : "white"}
			shadow="lg"
		>
			<Stack align="center" gap={0}>
				<Box
					style={{
						width: "100%",
						height: 80,
						borderTopLeftRadius: "var(--mantine-radius-md)",
						borderTopRightRadius: "var(--mantine-radius-md)",
						backgroundColor: isDark
							? "var(--mantine-color-dark-7)"
							: "var(--mantine-color-gray-2)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						position: "relative",
						overflow: "hidden",
					}}
				>
					<IconMapPin
						size={32}
						color={
							isDark
								? "var(--mantine-color-blue-4)"
								: "var(--mantine-color-blue-6)"
						}
					/>
					<Badge
						size="xs"
						radius="sm"
						color={isDark ? "blue.4" : "blue.7"}
						variant="filled"
						style={{
							position: "absolute",
							top: 8,
							left: 8,
						}}
						leftSection={<IconMapPin size={10} style={{ marginRight: -2 }} />}
					>
						Precise: On
					</Badge>
				</Box>
				<Stack align="center" gap={4} p="md" pb="sm">
					<Text
						size="md"
						fw={600}
						ta="center"
						c={isDark ? "white" : "dark"}
						lh={1.3}
					>
						Allow "Village Caregiving" to use your location?
					</Text>
					<Text size="xs" ta="center" c={isDark ? "gray.4" : "dimmed"}>
						electronic verification of visits and signatures
					</Text>
				</Stack>
				<Divider w="100%" color={isDark ? "dark.3" : "gray.3"} />
				<Stack gap={0} w="100%">
					<Button
						fullWidth
						variant="subtle"
						color="blue"
						radius={0}
						tabIndex={-1}
						size="sm"
					>
						Allow Once
					</Button>
					<Divider w="100%" color={isDark ? "dark.3" : "gray.3"} />
					<Box
						style={{
							animation: isDark
								? "iosBgPulseDark 2.5s ease-in-out infinite"
								: "iosBgPulse 2.5s ease-in-out infinite",
						}}
					>
						<Button
							fullWidth
							variant="subtle"
							color="blue"
							fw={600}
							radius={0}
							tabIndex={-1}
							size="sm"
						>
							Allow While Using App
						</Button>
					</Box>
					<Divider w="100%" color={isDark ? "dark.3" : "gray.3"} />
					<Button
						fullWidth
						variant="subtle"
						color="blue"
						radius={0}
						tabIndex={-1}
						size="sm"
					>
						Don’t Allow
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
}

function OnboardingPermissionDeniedPage({
	permission,
	os,
	currentStep,
	onOpenSettings,
	onRecheck,
	onSkip,
	onBack,
	recheckFailed,
	badge,
}: {
	permission: "notifications" | "location" | "microphone";
	os: "ios" | "android";
	currentStep: number;
	onOpenSettings: () => void;
	onRecheck: () => void;
	onSkip: () => void;
	onBack: () => void;
	recheckFailed: boolean;
	badge: string;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";
	const title =
		permission === "notifications"
			? "Notifications are blocked."
			: permission === "location"
				? "Location access is blocked."
				: "Microphone access is blocked.";
	const explanation =
		permission === "notifications"
			? "You won't get shift offers or reminders. Follow the walkthrough below."
			: permission === "location"
				? "You won't be able to clock in properly. Follow the walkthrough below."
				: "You won't be able to record client confirmations. Follow the walkthrough below.";
	const recheckMessage =
		permission === "notifications"
			? "Notifications are still off. Please follow the steps above."
			: permission === "location"
				? "Location is still off. Please follow the steps above."
				: "Microphone is still off. Please follow the steps above.";

	return (
		<OnboardingPageShell
			badge={badge}
			currentStep={currentStep}
			onBack={onBack}
		>
			<Stack gap="md" style={{ flex: 1 }} justify="space-between">
				<Stack gap="md">
					<Title order={1} fw={800} lh={1.1}>
						{title}
					</Title>
					<Text fz="sm">{explanation}</Text>
					<SettingsWalkthrough os={os} permission={permission} />
				</Stack>
				<Stack gap="xs">
					{recheckFailed && (
						<Text fz="sm" c="red" ta="center" fw={500}>
							{recheckMessage}
						</Text>
					)}
					<Button
						fullWidth
						variant={isDark ? "default" : "filled"}
						color={isDark ? "gray" : "dark"}
						size="lg"
						radius="xl"
						onClick={onOpenSettings}
						leftSection={<IconSettings size={20} />}
					>
						Open Settings
					</Button>
					<Button
						fullWidth
						variant="light"
						color={isDark ? "dimmed" : "dark"}
						size="lg"
						radius="xl"
						onClick={onRecheck}
					>
						I've turned it on
					</Button>
					<Button
						fullWidth
						variant="subtle"
						color="dimmed"
						size="sm"
						onClick={onSkip}
					>
						Skip for now
					</Button>
				</Stack>
			</Stack>
		</OnboardingPageShell>
	);
}

function getCompletionTitle(
	allGranted: boolean,
	someGranted: boolean,
	hasNextShift: boolean,
): string {
	if (allGranted) {
		return hasNextShift ? "You're ready for your shift." : "You're all set.";
	}
	if (someGranted) {
		return "You're almost set up.";
	}
	return "You're not set up.";
}

function getPermissionStatusText(
	notifications: boolean,
	location: boolean,
	microphone: boolean,
	hasNextShift: boolean,
): string {
	const granted = [notifications, location, microphone].filter(Boolean).length;
	const total = 3;
	let status: string;
	if (granted === total) {
		status = "All permissions are on.";
	} else if (granted === 0) {
		status = "No permissions were enabled.";
	} else {
		const missing: string[] = [];
		if (!notifications) missing.push("notifications");
		if (!location) missing.push("location");
		if (!microphone) missing.push("microphone");
		status = `${granted} of ${total} permissions on. Missing: ${missing.join(", ")}.`;
	}
	return hasNextShift
		? `${status} Here's what's next on your schedule.`
		: status;
}

function getStepIndex(step: OnboardingStep): number {
	switch (step) {
		case "welcome":
			return 0;
		case "notifications-info":
		case "notifications-prompt-preview":
		case "notifications-prompt":
		case "notifications-denied":
			return 1;
		case "location-info":
		case "location-prompt-preview":
		case "location-prompt":
		case "location-denied":
			return 2;
		case "microphone-info":
		case "microphone-prompt-preview":
		case "microphone-prompt":
		case "microphone-denied":
			return 3;
		case "sms-consent":
			return 4;
		case "complete":
			return 5;
	}
}
